import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'

import { Button } from '../components/ui/Button'
import { CodeInput, isCodeComplete } from '../components/ui/CodeInput'
import { Field } from '../components/ui/Field'
import { ApiError, verifyMfa } from '../lib/api'

export interface MfaChallengeProps {
  challengeToken: string
  expiresAt?: string
  onVerified: () => void
  onCancel: () => void
}

/**
 * The second step of sign-in.
 *
 * Reached only after a correct password. Nothing here is authenticated yet —
 * the challenge token grants no access and the server has issued no session.
 */
export function MfaChallenge({ challengeToken, expiresAt, onVerified, onCancel }: MfaChallengeProps) {
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [useRecovery])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await verifyMfa(challengeToken, code.trim())
      onVerified()
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail)
        // An expired challenge cannot be retried — the user has to start
        // from the password again, so say that rather than leaving them
        // typing fresh codes into a dead form.
        if (err.problem.code === 'mfa_challenge_invalid') {
          setTimeout(onCancel, 2500)
        }
      } else {
        setError('Could not reach the server. Check your connection and try again.')
      }
      setCode('')
      inputRef.current?.focus()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="lg-auth">
      <form className="lg-auth__card" onSubmit={handleSubmit} noValidate>
        <div>
          <h1>Two-factor authentication</h1>
          <p className="lg-muted" style={{ fontSize: 'var(--lg-text-sm)', marginTop: 'var(--lg-space-1)' }}>
            {useRecovery
              ? 'Enter one of your recovery codes.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </p>
        </div>

        {error && (
          <div className="lg-alert" role="alert">
            {error}
          </div>
        )}

        <Field
          label={useRecovery ? 'Recovery code' : 'Authentication code'}
          required
          hint={useRecovery ? 'Each code works once.' : undefined}
        >
          {(props) => (
            <CodeInput
              {...props}
              ref={inputRef}
              mode={useRecovery ? 'recovery' : 'totp'}
              value={code}
              onChange={setCode}
            />
          )}
        </Field>

        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={submitting}
          disabled={!isCodeComplete(useRecovery ? 'recovery' : 'totp', code)}
        >
          Verify
        </Button>

        <div className="lg-row" style={{ justifyContent: 'space-between' }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setUseRecovery(!useRecovery)
              setCode('')
              setError(null)
            }}
          >
            {useRecovery ? 'Use authenticator app' : 'Use a recovery code'}
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>

        {expiresAt && (
          <p className="lg-subtle" style={{ fontSize: 'var(--lg-text-xs)' }}>
            This sign-in attempt expires at{' '}
            {new Date(expiresAt).toLocaleTimeString(undefined, {
              hour: '2-digit',
              minute: '2-digit',
            })}
            .
          </p>
        )}
      </form>
    </div>
  )
}
